import { useState } from 'react';
import { useSearchParamsWithApiKey, useDebouncedEffect } from '../../tools';
import type { SelectorOption } from '../common';

interface UseGenerateLogoV4Params {
    prompt: string;
    tagLine: string;
    businessName: string;
    industry: string;
    businessDescription: string;
    mediaType: string;
    imageCount: string;
    style: string;
    priority: string;
    culture: SelectorOption;
    setPrompt: (prompt: string) => void;
    setTagLine: (tagLine: string) => void;
    setBusinessName: (businessName: string) => void;
    setIndustry: (industry: string) => void;
    setBusinessDescription: (businessDescription: string) => void;
    setStyle: (style: string) => void;
    setMediaType: (mediaType: string) => void;
    setImageCount: (imageCount: string) => void;
    setPriority: (priority: string) => void;
    setCulture: (culture: SelectorOption) => void;
}

export const useGenerateLogoV4Params = (): UseGenerateLogoV4Params => {
    const [searchParams, setSearchParams] = useSearchParamsWithApiKey();
    const promptParam = searchParams.get('prompt');
    const tagLineParam = searchParams.get('tagLine');
    const businessNameParam = searchParams.get('businessName');
    const industryParam = searchParams.get('industry');
    const businessDescriptionParam = searchParams.get('businessDescription');
    const styleParam = searchParams.get('style');
    const mediaTypeParam = searchParams.get('mediaType');
    const imageCountParam = searchParams.get('imageCount');
    const priorityParam = searchParams.get('priority');
    const cultureParam = searchParams.get('culture');

    const [culture, setCulture] = useState<SelectorOption>(
        cultureParam ? { label: cultureParam, value: cultureParam } : { label: 'en-us', value: 'en-us' },
    );
    const [prompt, setPrompt] = useState<string>(promptParam ?? '');
    const [tagLine, setTagLine] = useState<string>(tagLineParam ?? '');
    const [businessName, setBusinessName] = useState<string>(businessNameParam ?? '');
    const [industry, setIndustry] = useState<string>(industryParam ?? '');
    const [businessDescription, setBusinessDescription] = useState<string>(businessDescriptionParam ?? '');
    const [style, setStyle] = useState(styleParam ?? '');
    const [mediaType, setMediaType] = useState<string>(mediaTypeParam ?? '');
    const [imageCount, setImageCount] = useState<string>(imageCountParam ?? '1');
    const [priority, setPriority] = useState<string>(priorityParam ?? 'cost');

    useDebouncedEffect(
        () => {
            const searchParamsObj: Record<string, string | string[]> = {};

            if (prompt) {
                searchParamsObj['prompt'] = prompt;
            }
            if (tagLine) {
                searchParamsObj['tagLine'] = tagLine;
            }
            if (businessName) {
                searchParamsObj['businessName'] = businessName;
            }
            if (industry) {
                searchParamsObj['industry'] = industry;
            }
            if (businessDescription) {
                searchParamsObj['businessDescription'] = businessDescription;
            }
            if (style) {
                searchParamsObj['style'] = style;
            }
            if (mediaType) {
                searchParamsObj['mediaType'] = mediaType;
            }
            if (imageCount) {
                searchParamsObj['imageCount'] = imageCount;
            }
            if (priority) {
                searchParamsObj['priority'] = priority;
            }
            if (culture) {
                searchParamsObj['culture'] = culture.value;
            }

            setSearchParams(searchParamsObj);
        },
        [
            prompt,
            tagLine,
            businessName,
            industry,
            businessDescription,
            style,
            mediaType,
            imageCount,
            priority,
            culture,
            setSearchParams,
        ],
        500,
    );

    return {
        prompt,
        tagLine,
        businessName,
        industry,
        businessDescription,
        style,
        mediaType,
        imageCount,
        priority,
        culture,
        setPrompt,
        setTagLine,
        setBusinessName,
        setIndustry,
        setBusinessDescription,
        setStyle,
        setMediaType,
        setImageCount,
        setPriority,
        setCulture,
    };
};
